// Auth helpers (PRD §9.4 + docs/frontend_architecture.md §6).
//
// Thin wrappers over the typed client that keep the in-memory authStore
// in sync. Mutations only — server reads route through TanStack Query
// hooks (api/queries/me.ts will land in B1f).
//
// The refresh timer lives in hooks/useAuthRefreshTimer.ts and reacts to
// `expiresAt` updates in the store, so these helpers do not schedule
// timers themselves — they just publish state and let the hook pick it
// up. Keeping scheduling in React-land makes lifecycle tear-down clean
// in tests and during HMR.

import { useAuthStore, type AuthUser } from '../stores/authStore';
import { client } from './client';

function expiresAtFromTtl(ttlSeconds: number): number {
  return Date.now() + ttlSeconds * 1000;
}

export async function login(
  email: string,
  password: string
): Promise<AuthUser> {
  const { data } = await client.POST('/api/v1/auth/login', {
    body: { email, password },
  });
  if (!data) throw new Error('login: empty response');
  useAuthStore.getState().setToken(data.access_token, expiresAtFromTtl(data.expires_in));
  const me = await fetchMe();
  useAuthStore.getState().setUser(me);
  return me;
}

// Coalesces parallel callers into one /auth/refresh round-trip. The 401
// interceptor (client.ts) and the refresh timer can race during expiry;
// coalescing here means the backend sees a single refresh request and
// every awaiter shares the same outcome.
let inflightRefresh: Promise<void> | null = null;

export function refresh(): Promise<void> {
  if (inflightRefresh) return inflightRefresh;
  const p = doRefresh();
  inflightRefresh = p;
  // Reset after the in-flight promise settles so the *next* refresh
  // creates a fresh promise. A `.catch(() => {})` keeps the cleanup
  // chain from emitting an unhandled-rejection warning when callers
  // also handle the same rejection.
  void p
    .catch(() => {
      /* swallow — every awaiter already saw the failure */
    })
    .finally(() => {
      if (inflightRefresh === p) inflightRefresh = null;
    });
  return p;
}

async function doRefresh(): Promise<void> {
  const { data } = await client.POST('/api/v1/auth/refresh');
  if (!data) throw new Error('refresh: empty response');
  useAuthStore.getState().setToken(data.access_token, expiresAtFromTtl(data.expires_in));
}

export async function logout(): Promise<void> {
  try {
    await client.POST('/api/v1/auth/logout');
  } finally {
    // Always clear local state, even if the server call failed — the
    // user wanted out and the refresh cookie is httpOnly so we can't
    // delete it from JS anyway. Worst case the server-side refresh row
    // lingers until expiry; the next login rotates it.
    useAuthStore.getState().clearSession();
  }
}

export async function fetchMe(): Promise<AuthUser> {
  const { data } = await client.GET('/api/v1/auth/me');
  if (!data) throw new Error('fetchMe: empty response');
  return data;
}
